import * as React from 'react';
import SelectWithAllToggle from '../common/SelectWithAllToggle';
import InternalTeamSelect from '../internal_team_select/InternalTeamSelect';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';

// Which internal teams a project is scoped to (API field: supplierFacilityIds).
// Shared by ProjectCreateForm and ProjectEditForm, which are otherwise near
// duplicates where a pasted field would drift.
//
// "Select All" is the empty list — the API reads no ids as "every internal tech of
// the buyer company", which is how projects behaved before this field existed. Only
// a non-empty list narrows access, and the backend enforces that on reads, writes
// and list filters, cascading to the project's deliverables, tasks and notes.
//
// A side benefit of the toggle: with All on, the picker isn't mounted at all, so a
// buyer with no internal teams never meets a disabled, empty dropdown.
export const INTERNAL_TEAMS_ALL_FIELD = 'allInternalTeams';
export const INTERNAL_TEAMS_FIELD = 'supplierFacilityIds';

// Form initialValues for both fields, from a project record (or {} when creating).
export const getInternalTeamsInitialValues = (formData) => ({
	[INTERNAL_TEAMS_FIELD]: nullSafeGetOrElse(INTERNAL_TEAMS_FIELD, formData, []),
	[INTERNAL_TEAMS_ALL_FIELD]: nullSafeGetOrElse(INTERNAL_TEAMS_FIELD, formData, []).length === 0,
});

// The ids to send, given submitted form values. `null` means "don't narrow": create
// omits the field entirely, while update has to send an explicit empty array,
// because undefined is dropped by JSON.stringify and the backend would keep the
// project's existing list instead of clearing it.
export const getInternalTeamIdsFromValues = (values) =>
	values[INTERNAL_TEAMS_ALL_FIELD] ||
	nullSafeGetOrElse(INTERNAL_TEAMS_FIELD, values, []).length === 0
		? null
		: values[INTERNAL_TEAMS_FIELD];

const ProjectInternalTeamsField = ({ form }) => (
	<>
		<SelectWithAllToggle
			label="Internal Teams"
			name={INTERNAL_TEAMS_FIELD}
			allName={INTERNAL_TEAMS_ALL_FIELD}
			form={form}
			displaySelectAllWithLabel
			// Select All off with nothing picked would send "no ids", which the API reads as
			// every internal team — the opposite of what the field says. Block it instead.
			requiredIfNotAllSelected
			selectAllPlaceholder="All internal teams"
			defaultFormFieldStyle={{ maxWidth: 640 }}
		>
			<InternalTeamSelect />
		</SelectWithAllToggle>
		<div className="footnoteregular mb-4 text-character-secondary" style={{ maxWidth: 640 }}>
			Only the teams picked here will see this project, along with its deliverables, tasks and
			notes. Select All keeps it visible to every internal team.
		</div>
	</>
);

export default ProjectInternalTeamsField;
